import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, ExternalLink, Pause, Play, ShieldCheck, Star,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDate, formatDateTime } from "@/lib/dates";

/**
 * One account, read-only (board item 5).
 *
 * **Nothing here edits a profile.** The four buttons in the header are the same
 * four PATCHes the list already had — suspend, reactivate, verify, feature —
 * and they are the only writes this page performs. That is a decision, not an
 * unfinished state: an admin who can retype somebody's phone number is an admin
 * who can silently break the account they were asked to look at, and every
 * field below has an owner who is not us.
 *
 * **Empty is a state, not a failure.** A client has no plan and no quotes; a
 * courtesy Master has a subscription and no payments at all; a FixyBee with no
 * work yet has no reviews. Each section says so in its own words rather than
 * printing a dash or, worse, an error — those sentences are the ones an admin
 * actually reads, so they are translated like any other copy.
 *
 * **The AI numbers are the enforcer's numbers.** `actions_used / actions_quota`
 * is the count `core/ai_wrapper` refuses on, the minutes are the ones
 * `services/voice/budget` rations, and the market searches carry the day
 * because the cap is a daily one. The endpoint reads them through that code on
 * purpose — a panel showing an allowance nobody checks against is worse than
 * one showing nothing.
 */
export default function AdminUserDetail() {
  const { t, i18n } = useTranslation();
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: payload } = await api.get(`/admin/users/${userId}/detail`);
      setData(payload);
    } catch (e) {
      setError(errorMessage(e, t("adminUser.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}/${action}`);
      await load();
    } catch (e) {
      setError(errorMessage(e, t("adminUser.actionFailed")));
    } finally {
      setBusy(false);
    }
  };

  const locale = i18n.language?.startsWith("es") ? "es-US" : "en-US";
  const fmt = {
    date: (v, options) => formatDate(v, options) || null,
    dateTime: (v) => formatDateTime(v) || null,
    // Cents from Stripe, divided here — the API returns what Stripe reported.
    cents: (v) =>
      v === null || v === undefined
        ? null
        : new Intl.NumberFormat(locale, { style: "currency", currency: "USD" })
            .format(v / 100),
  };

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-user-detail">
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-2 text-sm text-smoke hover:text-worker"
          data-testid="back-to-users"
        >
          <ArrowLeft size={14} /> {t("adminUser.back")}
        </Link>

        {error && (
          <p className="mt-4 text-sm text-danger" data-testid="detail-error">{error}</p>
        )}

        {loading ? (
          <div className="mt-6 space-y-4">
            <div className="skeleton h-28" />
            <div className="skeleton h-40" />
          </div>
        ) : data ? (
          <>
            <Header data={data} t={t} fmt={fmt} busy={busy} onAct={act} />
            <div className="mt-6 grid gap-6 xl:grid-cols-2">
              <Identity data={data} t={t} fmt={fmt} />
              <Billing data={data} t={t} fmt={fmt} />
              <Activity data={data} t={t} />
              <AiUsage data={data} t={t} fmt={fmt} />
              <Legal data={data} t={t} fmt={fmt} />
              <Tickets data={data} t={t} fmt={fmt} />
            </div>
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
}

/* -------------------------------------------------------------------------- */

function Section({ title, testId, children, empty }) {
  return (
    <section className="card-base p-5" data-testid={testId}>
      <h2 className="font-display text-lg font-bold text-worker mb-4">{title}</h2>
      {empty ? <p className="text-sm text-smoke">{empty}</p> : children}
    </section>
  );
}

/** A label and a value. Renders the row only when there is something to show. */
function Row({ label, value, mono = false, testId }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex gap-4 py-1.5 border-b border-sand last:border-0" data-testid={testId}>
      <span className="w-44 shrink-0 text-xs uppercase tracking-widest text-smoke font-display pt-0.5">
        {label}
      </span>
      <span className={`text-sm text-worker break-all ${mono ? "font-mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function Stat({ label, value, hint, testId }) {
  return (
    <div className="px-4 py-2" data-testid={testId}>
      <p className="text-xs uppercase tracking-widest text-smoke font-display">{label}</p>
      <p className="font-mono text-xl text-worker">{value}</p>
      {hint && <p className="text-xs text-smoke mt-0.5">{hint}</p>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Identity plus the action row.
 *
 * The actions live at the top because they are the only interactive thing on
 * the page: hiding them among the read-only fields would make an admin hunt
 * for the one control that has consequences.
 */
function Header({ data, t, fmt, busy, onAct }) {
  const { user } = data;
  const profile = user.profile || {};
  const isProvider = profile.role === "provider";
  const avatar = profile.avatar_url || profile.business_logo_url;
  const suspended = user.status === "suspended";

  return (
    <div className="card-base p-5 mt-4" data-testid="detail-header">
      <div className="flex flex-wrap items-start gap-4">
        {avatar ? (
          <img
            src={avatar} alt=""
            className="h-16 w-16 rounded-full object-cover border border-sand"
          />
        ) : (
          <div className="h-16 w-16 rounded-full bg-linen border border-sand flex items-center justify-center font-display text-xl text-smoke">
            {(profile.full_name || user.email || "?").slice(0, 1).toUpperCase()}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <h1 className="font-display text-2xl font-bold text-worker">
            {profile.full_name || t("adminUser.noName")}
          </h1>
          <p className="text-sm text-smoke font-mono break-all">{user.email}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="badge" data-testid="detail-role">
              {profile.role ? t(`adminUser.role.${profile.role}`) : t("adminUser.noRole")}
            </span>
            <span
              className={`badge ${suspended ? "bg-danger text-cream" : user.status === "active" ? "bg-success text-cream" : ""}`}
              data-testid="detail-status"
            >
              {t(`adminUser.status.${user.status}`, { defaultValue: user.status })}
            </span>
            {profile.is_verified && (
              <span className="badge bg-success text-cream">
                {t("adminUser.verified")}
              </span>
            )}
            {profile.is_featured && (
              <span className="badge bg-hive text-worker">{t("adminUser.featured")}</span>
            )}
            {profile.is_admin && <span className="badge">{t("adminUser.role.admin")}</span>}
            {user.is_deleted && (
              <span className="badge bg-danger text-cream">{t("adminUser.deleted")}</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="detail-actions">
          {suspended ? (
            <button
              className="fx-btn fx-btn--ghost text-xs inline-flex items-center gap-1"
              disabled={busy} onClick={() => onAct("reactivate")}
              data-testid="detail-reactivate"
            >
              <Play size={14} /> {t("adminUser.reactivate")}
            </button>
          ) : (
            <button
              className="fx-btn fx-btn--ghost text-xs inline-flex items-center gap-1"
              disabled={busy} onClick={() => onAct("suspend")}
              data-testid="detail-suspend"
            >
              <Pause size={14} /> {t("adminUser.suspend")}
            </button>
          )}
          {isProvider && !profile.is_verified && (
            <button
              className="fx-btn fx-btn--ghost text-xs inline-flex items-center gap-1"
              disabled={busy} onClick={() => onAct("verify")}
              data-testid="detail-verify"
            >
              <ShieldCheck size={14} /> {t("adminUser.verify")}
            </button>
          )}
          {isProvider && (
            <button
              className="fx-btn fx-btn--ghost text-xs inline-flex items-center gap-1"
              disabled={busy}
              onClick={() => onAct(`feature?featured=${!profile.is_featured}`)}
              data-testid="detail-feature"
            >
              <Star size={14} className={profile.is_featured ? "fill-current" : ""} />
              {profile.is_featured ? t("adminUser.unfeature") : t("adminUser.feature")}
            </button>
          )}
        </div>
      </div>
      <p className="mt-4 text-xs text-smoke">{t("adminUser.readOnlyNote")}</p>
    </div>
  );
}

function Identity({ data, t, fmt }) {
  const { user } = data;
  const p = user.profile || {};
  const loc = p.location || {};
  const place = [loc.city, loc.state, loc.zip || loc.postal_code, loc.country]
    .filter(Boolean).join(", ");

  return (
    <Section title={t("adminUser.identity")} testId="section-identity">
      <Row label={t("adminUser.userId")} value={user.user_id} mono />
      <Row label={t("adminUser.company")} value={p.company_name} />
      <Row
        label={t("adminUser.phone")}
        value={p.phone ? (
          <span className="inline-flex items-center gap-2">
            <span className="font-mono">{p.phone}</span>
            <span className={`badge ${p.phone_verified ? "bg-success text-cream" : ""}`}>
              {p.phone_verified ? t("adminUser.phoneVerified") : t("adminUser.phoneUnverified")}
            </span>
          </span>
        ) : null}
      />
      <Row label={t("adminUser.location")} value={place || null} />
      <Row
        label={t("adminUser.language")}
        value={p.language ? t(`adminUser.lang.${p.language}`, { defaultValue: p.language }) : null}
      />
      <Row
        label={t("adminUser.publicPage")}
        value={p.public_slug ? (
          <Link className="underline hover:text-hive-ink" to={`/pro/${p.public_slug}`}>
            /{p.public_slug}
          </Link>
        ) : null}
      />
      <Row label={t("adminUser.joined")} value={fmt.dateTime(user.created_at)} />
      <Row
        label={t("adminUser.lastLogin")}
        value={fmt.dateTime(user.last_login) || t("adminUser.never")}
      />
      <Row
        label={t("adminUser.authProvider")}
        value={t(`adminUser.auth.${user.provider}`, { defaultValue: user.provider })}
      />
      <Row
        label={t("adminUser.emailVerified")}
        value={user.email_verified
          ? (fmt.dateTime(user.email_verified_at) || t("adminUser.yes"))
          : t("adminUser.no")}
      />
      <Row
        label={t("adminUser.verification")}
        value={p.is_verified
          ? t("adminUser.verifiedVia", {
              method: t(`adminUser.method.${p.verification_method}`, {
                defaultValue: p.verification_method || "—",
              }),
              date: fmt.date(p.verified_at) || "",
            }).trim()
          : t("adminUser.notVerified")}
      />
    </Section>
  );
}

/**
 * Plan, subscription and money.
 *
 * `is_manual` is shown as "Cortesía" / "Courtesy" because that is what it means
 * operationally — a tier the platform granted rather than one Stripe sold — and
 * such an account correctly has no payments at all. That is the empty state the
 * payments table has to read well for.
 */
function Billing({ data, t, fmt }) {
  const { billing } = data;
  const sub = billing.subscription;
  const plan = billing.plan;
  const isProvider = data.user.profile?.role === "provider";

  if (!isProvider && !billing.payments.length && !sub) {
    return (
      <Section
        title={t("adminUser.billing")} testId="section-billing"
        empty={t("adminUser.noBillingClient")}
      />
    );
  }

  return (
    <Section title={t("adminUser.billing")} testId="section-billing">
      <Row
        label={t("adminUser.plan")}
        value={plan ? (
          <span className="inline-flex flex-wrap items-center gap-2">
            <span className="font-display font-semibold">{plan.name}</span>
            {sub?.is_manual && (
              <span className="badge bg-hive text-worker" data-testid="courtesy-badge">
                {t("adminUser.courtesy")}
              </span>
            )}
            {sub?.is_trial && <span className="badge">{t("adminUser.trial")}</span>}
          </span>
        ) : t("adminUser.noPlan")}
      />
      <Row
        label={t("adminUser.subStatus")}
        value={sub ? t(`adminUser.subState.${sub.status}`, { defaultValue: sub.status }) : null}
      />
      <Row
        label={t("adminUser.period")}
        value={sub && (sub.current_period_start || sub.current_period_end)
          ? `${fmt.date(sub.current_period_start) || "—"} → ${fmt.date(sub.current_period_end) || "—"}`
          : null}
      />
      <Row
        label={t("adminUser.quotesPeriod")}
        value={sub ? t("adminUser.quotesPeriodValue", {
          used: sub.quotes_sent_this_period ?? 0,
          included: plan?.included_quotes_per_month ?? t("adminUser.unlimited"),
        }) : null}
      />
      <Row
        label={t("adminUser.extraCredits")}
        value={sub ? String(sub.extra_credits_remaining ?? 0) : null}
        mono
      />
      <Row
        label={t("adminUser.stripeCustomer")}
        value={billing.stripe_customer_id ? (
          <a
            className="inline-flex items-center gap-1 underline hover:text-hive-ink font-mono"
            href={`https://dashboard.stripe.com/customers/${billing.stripe_customer_id}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="stripe-customer-link"
          >
            {billing.stripe_customer_id} <ExternalLink size={12} />
          </a>
        ) : null}
      />

      <p className="mt-5 mb-2 text-xs uppercase tracking-widest text-smoke font-display">
        {t("adminUser.payments")}
      </p>
      {billing.payments.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <tbody>
              {billing.payments.map((pay) => (
                <tr key={pay.payment_id} className="border-t border-sand">
                  <td className="py-2 pr-3 whitespace-nowrap text-smoke text-xs">
                    {fmt.date(pay.created_at)}
                  </td>
                  <td className="py-2 pr-3 font-mono">{fmt.cents(pay.amount_total) || "—"}</td>
                  <td className="py-2 pr-3">
                    {t(`adminUser.purpose.${pay.purpose}`, { defaultValue: pay.purpose })}
                  </td>
                  <td className="py-2">
                    <span className={`badge ${pay.status === "paid" ? "bg-success text-cream" : pay.status === "failed" ? "bg-danger text-cream" : ""}`}>
                      {t(`adminUser.payState.${pay.status}`, { defaultValue: pay.status })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-smoke" data-testid="payments-empty">
          {sub?.is_manual ? t("adminUser.noPaymentsCourtesy") : t("adminUser.noPayments")}
        </p>
      )}
    </Section>
  );
}

function Activity({ data, t }) {
  const { activity } = data;
  const isProvider = data.user.profile?.role === "provider";

  return (
    <Section title={t("adminUser.activity")} testId="section-activity">
      <div className="flex flex-wrap divide-x divide-sand -mx-4">
        <Stat
          label={t("adminUser.jobsPosted")} value={activity.jobs_posted}
          hint={t("adminUser.jobsCompleted", { count: activity.jobs_completed })}
          testId="stat-jobs"
        />
        {isProvider && (
          <Stat
            label={t("adminUser.quotesSent")} value={activity.quotes_sent}
            hint={t("adminUser.quotesAccepted", { count: activity.quotes_accepted })}
            testId="stat-quotes"
          />
        )}
        {isProvider && (
          <Stat
            label={t("adminUser.rating")}
            value={activity.reviews_received
              ? activity.average_rating.toFixed(2)
              : "—"}
            hint={activity.reviews_received
              ? t("adminUser.reviewsCount", { count: activity.reviews_received })
              : t("adminUser.noReviews")}
            testId="stat-rating"
          />
        )}
        {!isProvider && (
          <Stat
            label={t("adminUser.reviewsWritten")} value={activity.reviews_written}
            testId="stat-reviews-written"
          />
        )}
      </div>

      {isProvider && activity.reviews_received > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="rating-breakdown">
          {["quality", "responsibility", "price"].map((k) => (
            <span key={k} className="chip">
              {t(`adminUser.dim.${k}`)} · {activity[`avg_${k}`].toFixed(2)}
            </span>
          ))}
        </div>
      )}

      {isProvider && (
        <>
          <p className="mt-5 mb-2 text-xs uppercase tracking-widest text-smoke font-display">
            {t("adminUser.categories")}
          </p>
          {activity.service_categories.length ? (
            <div className="flex flex-wrap gap-2" data-testid="service-categories">
              {activity.service_categories.map((c) => (
                <span key={c.category_id} className="chip">{c.name}</span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-smoke">{t("adminUser.noCategories")}</p>
          )}
        </>
      )}
    </Section>
  );
}

/**
 * What this account has spent of the platform's AI, this month.
 *
 * The quota can come back as -1, which means unlimited (an admin account). It
 * is printed as a dash rather than as a negative allowance, because "2 / -1"
 * says nothing to anyone.
 */
function AiUsage({ data, t, fmt }) {
  const ai = data.ai_usage;
  const unlimited = ai.actions_quota < 0;

  return (
    <Section title={t("adminUser.aiUsage")} testId="section-ai">
      <div className="flex flex-wrap divide-x divide-sand -mx-4">
        <Stat
          label={t("adminUser.aiActions")}
          value={unlimited ? `${ai.actions_used} / —` : `${ai.actions_used} / ${ai.actions_quota}`}
          hint={t(`adminUser.bucket.${ai.bucket}`, { defaultValue: ai.bucket })}
          testId="stat-ai-actions"
        />
        <Stat
          label={t("adminUser.voiceMinutes")}
          value={`${ai.voice_minutes_used} / ${ai.voice_minutes_allowance}`}
          testId="stat-voice"
        />
        <Stat
          label={t("adminUser.marketSearches")}
          value={ai.market_searches_month}
          hint={t("adminUser.marketToday", {
            today: ai.market_searches_today, cap: ai.market_searches_per_day,
          })}
          testId="stat-search"
        />
        <Stat
          label={t("adminUser.aiSpend")}
          value={`$${ai.spent_usd.toFixed(4)}`}
          testId="stat-ai-spend"
        />
      </div>
      <p className="mt-4 text-xs text-smoke">
        {t("adminUser.aiSince", { date: fmt.date(ai.month_start, { timeZone: "UTC" }) })}
      </p>
    </Section>
  );
}

function Legal({ data, t, fmt }) {
  const items = data.legal_acceptances;
  return (
    <Section
      title={t("adminUser.legal")} testId="section-legal"
      empty={items.length ? null : t("adminUser.noLegal")}
    >
      <ul className="space-y-2" data-testid="legal-list">
        {items.map((a, i) => (
          <li key={`${a.document}-${a.document_version}-${i}`} className="text-sm">
            <span className="font-display font-semibold text-worker">
              {t(`adminUser.doc.${a.document}`, { defaultValue: a.document })}
            </span>
            <span className="text-smoke">
              {" "}· v{a.document_version} · {fmt.dateTime(a.accepted_at)} ·{" "}
              {t(`adminUser.legalSource.${a.source}`, { defaultValue: a.source })}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Tickets({ data, t, fmt }) {
  const items = data.tickets;
  return (
    <Section
      title={t("adminUser.tickets")} testId="section-tickets"
      empty={items.length ? null : t("adminUser.noTickets")}
    >
      <ul className="space-y-2" data-testid="tickets-list">
        {items.map((ticket) => (
          <li key={ticket.ticket_id} className="text-sm">
            <span className="font-display font-semibold text-worker">{ticket.subject}</span>
            <span className="text-smoke">
              {" "}· {t(`adminTickets.category.${ticket.category}`, { defaultValue: ticket.category })}
              {" "}· {t(`adminTickets.status.${ticket.status}`, { defaultValue: ticket.status })}
              {" "}· {fmt.date(ticket.created_at)}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}
